import { Coordinates, CalculationMethod, PrayerTimes, Rounding } from "adhan";
import { LANG_TAG } from "@/lib/geo";

export const PRAYERS = [
  { key: "fajr", label: { en: "Fajr", id: "Subuh", ar: "الفجر" } },
  { key: "sunrise", label: { en: "Sunrise", id: "Terbit", ar: "الشروق" } },
  { key: "dhuhr", label: { en: "Dhuhr", id: "Dzuhur", ar: "الظهر" } },
  { key: "asr", label: { en: "Asr", id: "Ashar", ar: "العصر" } },
  { key: "maghrib", label: { en: "Maghrib", id: "Maghrib", ar: "المغرب" } },
  { key: "isha", label: { en: "Isha", id: "Isya", ar: "العشاء" } },
];
export const IMSAK_LABEL = { en: "Imsak", id: "Imsak", ar: "الإمساك" };
export const DEFAULT_METHOD = "auto";

// Kemenag RI (Bimas Islam): Subuh −20°, Isya −18°, seconds rounded UP, then ihtiyat: Subuh/Ashar/Isya +2,
// Dzuhur +3, Maghrib +3 (incl. horizon dip), Terbit −4. Verified against the official schedule for 10 cities
// (scripts/check_kemenag.js): ~87% exact, rest ±1 min. Imsak = Subuh − 10 min.
const KEMENAG_ADJ = { fajr: 2, sunrise: -4, dhuhr: 3, asr: 2, maghrib: 3, isha: 2 };
const inBox = (lat, lng, [s, w, n, e]) => lat >= s && lat <= n && lng >= w && lng <= e;
const REGIONS = [
  ["Kemenag", [-11.5, 94.5, 6.5, 141.5]],
  ["UmmAlQura", [16, 34.5, 32.5, 56]],
  ["Singapore", [1.1, 103.5, 1.6, 104.2]],
  ["Egyptian", [21.5, 24.5, 32, 37]],
  ["Karachi", [23, 60.5, 37.5, 92.5]],
  ["Turkey", [35.5, 25.5, 42.5, 45]],
  ["Qatar", [24.4, 50.6, 26.3, 51.8]],
  ["Dubai", [22.5, 51, 26.5, 56.5]],
  ["NorthAmerica", [14, -170, 72, -50]],
];

// "auto" → method by location (Indonesia → Kemenag, Saudi → Umm al-Qura, …, else MWL).
export function resolveMethod(id, lat, lng) {
  if (id && id !== "auto") return id;
  if (typeof lat !== "number" || typeof lng !== "number") return "MuslimWorldLeague";
  return (REGIONS.find(([, box]) => inBox(lat, lng, box)) || ["MuslimWorldLeague"])[0];
}
export const imsakFrom = (fajr) => (fajr ? new Date(fajr.getTime() - 10 * 60000) : null);

// Muezzin voices. Only "haram" ships a dedicated Fajr recording; others use one file.
export const MUEZZINS = [
  { id: "haram", src: "/adzan.mp3", fajrSrc: "/adzan-subuh.mp3", label: { en: "Masjidil Haram (Makkah)", id: "Masjidil Haram (Makkah)", ar: "المسجد الحرام (مكة)" } },
  { id: "madinah", src: "/adzan-madinah.mp3", label: { en: "Masjid Nabawi (Madinah)", id: "Masjid Nabawi (Madinah)", ar: "المسجد النبوي (المدينة)" } },
  { id: "afasy", src: "/adzan-afasy.mp3", label: { en: "Mishary Rashid Alafasy", id: "Mishary Rasyid Alafasy", ar: "مشاري راشد العفاسي" } },
  { id: "mesir", src: "/adzan-mesir.mp3", label: { en: "Mohamed Refaat (Egypt)", id: "Mohamed Refaat (Mesir)", ar: "محمد رفعت (مصر)" } },
  { id: "sobhi", src: "/adzan-islam-sobhi.mp3", label: { en: "Sheikh Islam Sobhi", id: "Syaikh Islam Sobhi", ar: "الشيخ إسلام صبحي" } },
];
export const muezzinById = (id) => MUEZZINS.find((m) => m.id === id) || MUEZZINS[0];
export const readMuezzin = (progress) => progress?.favorite_muezzin || localStorage.getItem("adzan_voice") || "haram";
export const adhanSoundOn = () => localStorage.getItem("adzan_sound") !== "off";

export function getParams(id) {
  if (id === "Kemenag") {
    const p = CalculationMethod.Other();
    p.fajrAngle = 20;
    p.ishaAngle = 18;
    p.rounding = Rounding.Up;
    p.adjustments = { ...KEMENAG_ADJ };
    return p;
  }
  const fn = CalculationMethod[id];
  return fn ? fn() : CalculationMethod.MuslimWorldLeague();
}

// Next prayer for [lat, lng]: earliest time after `now` across yesterday/today/tomorrow (robust when the
// device timezone differs from the location). Sunrise excluded by default.
export function nextPrayer([lat, lng], methodId = DEFAULT_METHOD, now = new Date(), { includeSunrise = false } = {}) {
  const c = new Coordinates(lat, lng);
  const params = getParams(resolveMethod(methodId, lat, lng));
  let best = null;
  for (const offset of [-1, 0, 1]) {
    const day = new Date(now);
    day.setDate(day.getDate() + offset);
    const pt = new PrayerTimes(c, day, params);
    for (const { key } of PRAYERS) {
      if (key === "sunrise" && !includeSunrise) continue;
      const at = pt[key];
      if (at > now && (!best || at < best.at)) best = { key, at };
    }
  }
  return best;
}

export function fmtCountdown(ms) {
  let s = Math.max(0, Math.floor(ms / 1000));
  const h = String(Math.floor(s / 3600)).padStart(2, "0");
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  return `${h}:${m}:${String(s % 60).padStart(2, "0")}`;
}

// One clock format for every prayer-time display (dashboard card, /times page, dialogs, monthly table).
// `tz` = IANA zone of the LOCATION, so times read like the local clock there even when the device is elsewhere.
export const fmtTime = (d, lang, tz) => (d ? d.toLocaleTimeString(LANG_TAG[lang] || "en-US", { hour: "2-digit", minute: "2-digit", ...(tz ? { timeZone: tz } : {}) }) : "");

// "GMT+3" style label of `tz` at instant `d`.
export function zoneOffsetLabel(d, tz) {
  try {
    return new Intl.DateTimeFormat("en", { timeZone: tz, timeZoneName: "shortOffset" }).formatToParts(d).find((p) => p.type === "timeZoneName")?.value || tz;
  } catch { return tz; }
}

// Calendar date (Y/M/D) at instant `d` in zone `tz`, returned as a device-local midnight Date — the form the
// adhan library expects (it reads local date components).
export function zoneDate(d, tz) {
  try {
    const parts = new Intl.DateTimeFormat("en", { timeZone: tz, year: "numeric", month: "numeric", day: "numeric" }).formatToParts(d);
    const get = (t) => Number(parts.find((p) => p.type === t)?.value);
    return new Date(get("year"), get("month") - 1, get("day"));
  } catch { return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }
}

// Full-day schedule (incl. Imsak for Kemenag) for one calendar date — used by the monthly table.
export function dayTimes([lat, lng], date, resolvedMethod, withImsak = resolvedMethod === "Kemenag") {
  const pt = new PrayerTimes(new Coordinates(lat, lng), date, getParams(resolvedMethod));
  return { imsak: withImsak ? imsakFrom(pt.fajr) : null, fajr: pt.fajr, sunrise: pt.sunrise, dhuhr: pt.dhuhr, asr: pt.asr, maghrib: pt.maghrib, isha: pt.isha };
}

// Today's six times for [lat, lng] as [{ key, label, at }] in PRAYERS order.
export function todayTimes([lat, lng], methodId = DEFAULT_METHOD, now = new Date()) {
  const pt = new PrayerTimes(new Coordinates(lat, lng), now, getParams(resolveMethod(methodId, lat, lng)));
  return PRAYERS.map((p) => ({ ...p, at: pt[p.key] }));
}

// When the "next prayer" changes, return the prayer whose time just passed (within `windowMs`), else null.
export function prayerJustPassed(prev, next, nowMs = Date.now(), windowMs = 90000) {
  if (!prev || !next || prev.at === next.at) return null;
  const passed = nowMs - prev.at;
  return passed >= 0 && passed <= windowMs ? prev.key : null;
}
